import { type FC, useId } from 'react';
import { Box, Divider, Skeleton, Stack } from '@mui/material';

import type {
  DashboardWidgetConfig,
  WidgetChartConfigCamelizedDefault,
} from 'src/types/dashboard';
import FilterDropdown from 'src/components/dashboard/chartComponents/FilterDropdown';
import { useExportOptions } from 'src/components/dashboard/charts/LineChart/hooks';
import LineChart from 'src/components/dashboard/charts/LineChart/LineChart';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import useWidgetChartConfig from 'src/components/dashboard/hooks/useWidgetChartConfig';
import useWidgetFilterConfig from 'src/components/dashboard/hooks/useWidgetFilterConfig';
import type { AggregateList } from 'src/components/followUp/common/types';
import NoResultsToShowAdjustFiltersWarning from 'src/components/followUp/common/warning/NoResultsToShowAdjustFiltersWarning';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

interface BaseTrendAnalysisWidgetProps {
  config: DashboardWidgetConfig;
  endpointPath: string;
  chartConfigDefault?: WidgetChartConfigCamelizedDefault;
}

const BaseTrendAnalysisWidget: FC<BaseTrendAnalysisWidgetProps> = ({
  config,
  endpointPath,
  chartConfigDefault,
}) => {
  const ariaId = useId();
  const { filters, setFilters, filterConfig } = useWidgetFilterConfig(config);
  const chartConfig = useWidgetChartConfig(config, chartConfigDefault);

  const endpoint = useGenerateWidgetEndpoint(endpointPath, config.id);

  const hasValidFilter = !!filters.time_step;
  const { data, isFetching, errorMsg, refetch, queryFilters } =
    useWidgetQuery<AggregateList>(
      endpoint,
      { extraFilters: filters },
      hasValidFilter,
    );

  const exportOptions = useExportOptions({
    data,
    filters,
    widgetName: config.name,
    queryFilters,
  });

  return (
    <WidgetFrame
      ariaId={ariaId}
      loading={(isFetching || !data) && hasValidFilter}
      refetch={refetch}
      exportOptions={exportOptions}
      error={errorMsg}
      header={<WidgetHeaderWithPill title={config.name} ariaId={ariaId} />}
      footer={!data?.totalObjects && <NoResultsToShowAdjustFiltersWarning />}
    >
      <Stack direction="column" sx={{ height: '100%', width: '100%' }}>
        {!!filterConfig.timeStepFilterConfig && (
          <Box sx={{ pt: 2, mb: 4, px: 2.75 }}>
            <Box sx={{ pb: 1.5 }}>
              <FilterDropdown
                filterConfig={filterConfig.timeStepFilterConfig}
                setFilters={setFilters}
                selectedFilters={filters}
              />
            </Box>
            <Divider />
          </Box>
        )}
        <Box sx={{ width: '100%', height: '100%', px: 4 }}>
          {data && Array.isArray(data.results) ? (
            <LineChart
              aggregateResults={data.results}
              chartConfig={chartConfig}
            />
          ) : (
            <Stack direction="column" sx={{ height: '100%' }}>
              <Skeleton variant="rectangular" height="100%" />
              <Skeleton variant="text" height={60} />
            </Stack>
          )}
        </Box>
      </Stack>
    </WidgetFrame>
  );
};

export default BaseTrendAnalysisWidget;
